import { computed } from '@angular/core';
import { signalStore, withComputed, withMethods } from '@ngrx/signals';
import { withReplicationPool } from './replication-pool.feature';

/** The role-independent part of a Horde replication request; the role is supplied by `host()`/`join()`. */
interface HordeConnectRequest {
    gameId: string;
    playerIds: readonly string[];
    scopeId: string | null;
}

/**
 * Root SignalStore owning the scoped Horde-protocol replication pools for the active game. All pool lifecycle,
 * queueing, bounded retry, and status/diagnostics reporting come from the shared {@link withReplicationPool}
 * feature; this store only pins the Horde-specific `start` (the multi-topic scoped replication plan) and the
 * host/joiner request shapes.
 */
export const HordeReplicationStore = signalStore(
    { providedIn: 'root' },
    withReplicationPool<HordeConnectRequest>({
        label: 'horde',
        start: async (request, { getDatabase, role, signalingUrl }) => {
            const { startHordeWebRTCReplication } = await import('./horde-webrtc-replication');

            return startHordeWebRTCReplication({
                db: getDatabase(),
                request: { ...request, role },
                signalingServerUrl: signalingUrl,
            });
        },
    }),
    withComputed((store) => ({
        activeGameId: computed(() => store.activeRequest()?.gameId ?? null),
        isHost: computed(() => store.role() === 'host'),
    })),
    withMethods((store) => ({
        /** Starts authoritative replication as the host for the selected game. */
        host(gameId: string, playerIds: readonly string[]): void {
            store._connect({ gameId, playerIds, scopeId: null }, 'host');
        },

        /** Starts scoped replication as a player joining the selected game. */
        join(gameId: string, scopeId: string | null): void {
            store._connect({ gameId, playerIds: [], scopeId }, 'joiner');
        },
    }))
);
